import { Box, Button, Radio } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import CustomTextArea from "../../../../../components/CustomTextArea";
import Flex from "../../../../../components/Flex";
import StyledDialog from "../../../../../components/StyledDialog";
import Text from "../../../../../components/Text";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { PATIENT_INQUIRY_STATUS_COUNTS_KEY } from "../../../../../queries/collaboration/patient-inquiry/use-get-patient-inquiry-status-counts";
import { useUpdatePatientInquiryStatus } from "../../../../../queries/collaboration/patient-inquiry/use-update-patient-inquiry-status";
import { PatientInquiryStatusUpdateStatusEnum } from "../../../../../services";
import { formatDate, formatTime } from "../../../../../utils/date-time-utility";
import { getXtenantId, showToastOnApiCall } from "../../../../../utils/utils";
import { CANCEL_OPTIONS } from "./patient-inquiry-constants";

interface DenyAppointmentModalProps {
  disclosureHost: DisclosureHost;
  appointmentId: string;
  patientName?: string;
  providerName?: string;
  appointmentDateTime?: string;
  onDenied: () => void;
}

const DenyAppointmentModal = ({
  disclosureHost,
  appointmentId,
  patientName,
  providerName,
  appointmentDateTime,
  onDenied,
}: DenyAppointmentModalProps) => {
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { mutateAsync: updateInquiryStatus } = useUpdatePatientInquiryStatus();

  const [isWidgetApproved, setIsWidgetApproved] = useState<boolean | null>(
    null
  );
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");

  const handleClose = () => {
    setIsWidgetApproved(null);
    setReason("");
    setReasonError("");
    disclosureHost.close();
  };

  const handleDeny = () => {
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setReasonError("Reason is a required field");
      return;
    }
    if (isWidgetApproved === null) return;

    /** on failure the toast is shown and the modal stays open to retry */
    showToastOnApiCall({
      apiCall: updateInquiryStatus({
        inquiryId: appointmentId,
        patientInquiryStatusUpdate: {
          status: PatientInquiryStatusUpdateStatusEnum.Rejected,
          rejectReason: trimmedReason,
          isWidgetApproved,
        },
        xTENANTID: getXtenantId(),
      }),
      showSnackbar,
      onSave: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ["collaboration-patient-inquiry-list"],
          }),
          queryClient.invalidateQueries({
            queryKey: ["patient-inquiry-by-id"],
          }),
          // Denying clears a pending inquiry, so the scheduling badge count is
          // refetched rather than left showing the pre-deny number.
          queryClient.invalidateQueries({
            queryKey: [PATIENT_INQUIRY_STATUS_COUNTS_KEY],
          }),
        ]);
        handleClose();
        onDenied();
      },
    });
  };

  const InfoRow = ({ label, value }: { label: string; value: string }) => (
    <Flex gap={1} flexWrap="wrap">
      <Text color="#616161" minWidth="140px">
        {label}
      </Text>
      <Text color="#616161">:</Text>
      <Text>{value || "-"}</Text>
    </Flex>
  );

  return (
    <StyledDialog
      open={disclosureHost.opened}
      onClose={handleClose}
      dialogTitle="Deny Appointment"
      PaperProps={{ sx: { width: "560px" } }}
      dialogAction={
        <>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={isWidgetApproved === null}
            onClick={handleDeny}
          >
            Deny
          </Button>
        </>
      }
    >
      <Flex flexDirection="column" gap={2}>
        <Box
          sx={{
            border: "1px solid #E0E0E0",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <Box bgcolor="#F5F6F8" px={1.5} py={1}>
            <Text fontWeight="medium">Appointment Request</Text>
          </Box>
          <Flex flexDirection="column" gap={1} p={1.5}>
            <InfoRow label="Patient Name" value={patientName || "-"} />
            <InfoRow label="Provider" value={providerName || "-"} />
            <InfoRow
              label="Requested Date"
              value={
                appointmentDateTime
                  ? (formatDate(appointmentDateTime) ?? "-")
                  : "-"
              }
            />
            <InfoRow
              label="Slot Time"
              value={
                appointmentDateTime ? formatTime(appointmentDateTime) : "-"
              }
            />
          </Flex>
        </Box>

        <Flex flexDirection="column" gap={1}>
          <Text fontWeight="medium">
            Select what should be cancelled
            <Text component="span" color="error">
              {" *"}
            </Text>
          </Text>

          {CANCEL_OPTIONS.map((option) => {
            const isSelected = isWidgetApproved === option.isWidgetApproved;
            return (
              <Flex
                key={option.label}
                gap={1}
                p={1.5}
                alignItems="flex-start"
                onClick={() => setIsWidgetApproved(option.isWidgetApproved)}
                sx={{
                  cursor: "pointer",
                  borderRadius: 1,
                  border: `1px solid ${isSelected ? "#6C4CE3" : "#E0E0E0"}`,
                  bgcolor: isSelected ? "#F7F4FF" : "transparent",
                }}
              >
                <Radio
                  size="small"
                  sx={{ p: 0, mt: "2px" }}
                  checked={isSelected}
                  name="deny-cancel-option"
                  onChange={() => setIsWidgetApproved(option.isWidgetApproved)}
                />
                <Box>
                  <Text fontWeight="medium">{option.label}</Text>
                  <Text color="#616161">{option.description}</Text>
                </Box>
              </Flex>
            );
          })}
        </Flex>

        <CustomTextArea
          required
          label="Reason for Denial"
          value={reason}
          minRows={4}
          placeholder="Enter reason here..."
          error={reasonError as any}
          onChange={(e) => {
            setReason(e.currentTarget.value);
            if (reasonError) setReasonError("");
          }}
        />
      </Flex>
    </StyledDialog>
  );
};

export default DenyAppointmentModal;
